// src/pages/Techniques.js
import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { fadeIn, slideInLeft, slideInRight, scaleIn } from '../styles/animations';
import SectionTitle from '../components/ui/SectionTitle';

const TechniquesPageWrapper = styled.div`
  padding-top: 80px; // Pour compenser la hauteur du header fixe
`;

const HeroSection = styled.section`
  background-color: ${props => props.theme.colors.primary};
  padding: ${props => props.theme.spacing.xl} 0;
  color: ${props => props.theme.colors.light};
  text-align: center;
`;

const HeroContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 0 ${props => props.theme.spacing.md};
  animation: ${fadeIn} 0.8s ease-out forwards;
`;

const HeroTitle = styled.h1`
  color: ${props => props.theme.colors.light};
  margin-bottom: ${props => props.theme.spacing.md};
  font-size: ${props => props.theme.fontSizes.xxxl};
  
  @media (max-width: ${props => props.theme.breakpoints.sm}) {
    font-size: ${props => props.theme.fontSizes.xxl};
  }
`;

const HeroSubtitle = styled.p`
  font-size: ${props => props.theme.fontSizes.large};
  margin-bottom: 0;
  opacity: 0.9;
  color: ${props => props.theme.colors.light};
`;

const Section = styled.section`
  padding: ${props => props.theme.spacing.xl} 0;
  background-color: ${props => props.bgColor || props.theme.colors.light};
`;

const SectionContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 ${props => props.theme.spacing.md};
`;

const TechniqueTitle = styled.h2`
  font-size: ${props => props.theme.fontSizes.xl};
  color: ${props => props.theme.colors.primary};
  margin-bottom: ${props => props.theme.spacing.md};
  position: relative;
  
  &:after {
    content: '';
    position: absolute;
    bottom: -10px;
    left: 0;
    width: 50px;
    height: 3px;
    background-color: ${props => props.theme.colors.primary};
  }
`;

const TechniqueContent = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${props => props.theme.spacing.xl};
  margin-top: ${props => props.theme.spacing.lg};
  
  @media (max-width: ${props => props.theme.breakpoints.md}) {
    grid-template-columns: 1fr;
    gap: ${props => props.theme.spacing.lg};
  }
`;

const TextContent = styled.div`
  animation: ${slideInLeft} 0.8s ease-out forwards;
  opacity: 0;
  
  p {
    margin-bottom: ${props => props.theme.spacing.md};
    line-height: 1.7;
  }
  
  ul {
    margin-bottom: ${props => props.theme.spacing.md};
    padding-left: 1.5rem;
    
    li {
      margin-bottom: ${props => props.theme.spacing.xs};
    }
  }
`;

const ImageContent = styled.div`
  animation: ${slideInRight} 0.8s ease-out forwards;
  opacity: 0;
`;

const MainImage = styled.img`
  width: 100%;
  height: auto;
  max-height: 325px;
  object-fit: cover;
  border-radius: ${props => props.theme.borderRadius.medium};
  box-shadow: ${props => props.theme.shadows.medium};
`;

const GalleryContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: ${props => props.theme.spacing.sm};
  margin-top: ${props => props.theme.spacing.md};
  
  @media (max-width: ${props => props.theme.breakpoints.md}) {
    grid-template-columns: repeat(3, 1fr);
  }
  
  @media (max-width: ${props => props.theme.breakpoints.sm}) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const GalleryImage = styled.img`
  width: 100%;
  height: 100px;
  object-fit: cover;
  border-radius: ${props => props.theme.borderRadius.small};
  box-shadow: ${props => props.theme.shadows.small};
  cursor: pointer;
  transition: all ${props => props.theme.transitions.fast};
  
  &:hover {
    transform: scale(1.05);
    box-shadow: ${props => props.theme.shadows.medium};
  }
`;

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.8);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  opacity: 0;
  visibility: hidden;
  transition: opacity 0.3s ease;
  
  ${props => props.isOpen && `
    opacity: 1;
    visibility: visible;
  `}
`;

const ModalContent = styled.div`
  max-width: 90%;
  max-height: 90%;
  position: relative;
  
  img {
    max-width: 100%;
    max-height: 90vh;
    border-radius: ${props => props.theme.borderRadius.medium};
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: -40px;
  right: 0;
  background: none;
  border: none;
  color: white;
  font-size: 2rem;
  cursor: pointer;
`;

const Techniques = () => {
  const [selectedImage, setSelectedImage] = useState(null);

  const openModal = (imageSrc) => {
    setSelectedImage(imageSrc);
  };

  const closeModal = () => {
    setSelectedImage(null);
  };

  const finitionsImages = [
    "605a2c687500001272fe3a93.webp",
    "20140122_0655.webp",
    "images_1.webp",
    "IMG_0593-1024x768-2560x1200.jpg",
  ];

  const fixationsImages = [
    "20241119_110721.webp",
    "605a2c687500001272fe3a93.webp",
    "images_2.webp",
    "IMG_0593-1024x768-2560x1200.jpg",
  ];

  return (
    <TechniquesPageWrapper>
      <Helmet>
        <title>Nos Techniques - Decko | Créateur de vos parquets de jardin</title>
        <meta name="description" content="Découvrez nos techniques de pose, finitions et fixations invisibles pour vos terrasses en bois et parquets de jardin." />
      </Helmet>

      <HeroSection>
        <HeroContainer>
          <HeroTitle>Nos Techniques</HeroTitle>
          <HeroSubtitle>Savoir-faire et excellence pour des réalisations durables</HeroSubtitle>
        </HeroContainer>
      </HeroSection>

      <Section>
        <SectionContainer>
          <TechniqueTitle>Les détails de finitions</TechniqueTitle>

          <TechniqueContent>
            <TextContent>
              <p>
                Chez Decko, nous accordons une importance particulière aux finitions, car ce sont ces détails qui font toute la différence dans la qualité d'une réalisation.
              </p>
              <p>
                Nos terrasses et parquets de jardin sont conçus avec un souci constant du détail :
              </p>
              <ul>
                <li>Coupes d'onglets parfaites pour les angles</li>
                <li>Alignement précis des lames de bois</li>
                <li>Espacement régulier pour permettre la dilatation naturelle du bois</li>
                <li>Bordures soigneusement finies pour un rendu esthétique</li>
                <li>Ponçage des surfaces pour un confort optimal pieds nus</li>
              </ul>
              <p>
                Cette attention portée aux finitions garantit non seulement une esthétique irréprochable, mais également une durabilité accrue de vos installations.
              </p>
            </TextContent>

            <ImageContent>
              <MainImage
                src="20230925_125657.webp"
                alt="Détails de finitions Decko"
              />

              <GalleryContainer>
                {finitionsImages.map((image, index) => (
                  <GalleryImage
                    key={index}
                    src={image}
                    alt={`Finition Decko ${index + 1}`}
                    onClick={() => openModal(image)}
                  />
                ))}
              </GalleryContainer>
            </ImageContent>
          </TechniqueContent>
        </SectionContainer>
      </Section>

      <Section bgColor={props => props.theme.colors.gray}>
        <SectionContainer>
          <TechniqueTitle>Les fixations invisibles</TechniqueTitle>

          <TechniqueContent>
            <ImageContent>
              <MainImage
                src="IMG_0554-1024x768-2560x1200.jpg"
                alt="Fixations invisibles Decko"
              />

              <GalleryContainer>
                {fixationsImages.map((image, index) => (
                  <GalleryImage
                    key={index}
                    src={image}
                    alt={`Fixation invisible Decko ${index + 1}`}
                    onClick={() => openModal(image)}
                  />
                ))}
              </GalleryContainer>
            </ImageContent>

            <TextContent>
              <p>
                Les clous ont disparu de la surface des parquets modernes et même des parquets massifs. DeckO Créateur de parquets de jardin peut aussi construire sans vis apparente. Nous utilisons les meilleurs procédés pour une esthétique incomparable et durable.
              </p>
              <p>
                Notre système de fixation invisible présente de nombreux avantages :
              </p>
              <ul>
                <li>Esthétique impeccable sans aucune vis visible</li>
                <li>Aucun risque de blessure aux pieds nus</li>
                <li>Elimination des zones de rétention d'eau autour des vis</li>
                <li>Réduction du risque de fissuration du bois</li>
                <li>Durabilité accrue de l'installation</li>
              </ul>
              <p>
                Attention : en inox ou en polymères, les systèmes discrets testés et éprouvés ne sont pas nombreux. Demandez-nous conseil.
              </p>
            </TextContent>
          </TechniqueContent>
        </SectionContainer>
      </Section>

      <Modal isOpen={selectedImage !== null} onClick={closeModal}>
        <ModalContent onClick={e => e.stopPropagation()}>
          <CloseButton onClick={closeModal}>&times;</CloseButton>
          {selectedImage && <img src={selectedImage} alt="Agrandissement" />}
        </ModalContent>
      </Modal>

    </TechniquesPageWrapper>
  );
};

export default Techniques;